import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';

import { queryClient } from '@/adapters/query';
import {
  SCREENER_SETTINGS_BATCH_KEY,
  SCREENER_SETTINGS_PER_FACILITY_KEY
} from '@/hooks/screener-settings/useFacilityScreenerSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DATA_CENTER_V3_QUERY_KEY } from '@/hooks/useDataCenterV3Query';
import {
  ALL_FACILITIES,
  FacilityPayload,
  selectedFacilityIdAtom,
  USE_FACILITIES_QUERY_KEY,
  useFacilitiesQuery,
  useSelectedFacilityId
} from '@/hooks/useFacilitiesQuery';
import { useAccessibleFacilityIds } from '@/pages/CRM/Screener/hooks/useInstanceAccessibility';
import { FIND_ONE_SCREENER_SETTING_QUERY_KEY } from '@/pages/CRM/Screener/hooks/useScreenerSettingsQuery';
import { isDataCenterV3Path } from '@/pages/DataCenter/v3/dataCenterPage';
import { RoutePath } from '@/routes/RoutePath';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';
import { atomWithSession } from '@/utils/sessionStorageAtom';

import { getDefaultScreenerFacilityId } from './defaultScreenerView';
import {
  getMinimumFacilityCountForAllView,
  isAutoDecisioningAdminHubPath
} from './screenerFacilityTypeHelpers';

export const ALL_FACILITIES_PARAM = 'all_facilities';
export const FACILITY_TYPE_PARAM = 'facility_type';
export const SET_FACILITY_ID_PARAM = 'set_facility_id';
export const SCREENER_FACILITY_VIEW_KIND = {
  Specific: 'specific',
  AllFacilities: 'all-facilities'
} as const;

// Adding a route here only flips on the all-facilities view derivation. The
// page also needs: data queries that handle a missing facility id, and UI that
// can render rows from multiple facilities (e.g. a facility column).
export const SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT: RoutePath[] = [
  RoutePath.CRMScreenerAllLeads,
  RoutePath.CRMScreenerESign,
  RoutePath.CRMTasks,
  RoutePath.CRMScreenerEfax,
  RoutePath.CRMScreenerInbound,
  RoutePath.CRMAuthorizationsPriorAuthorizations,
  RoutePath.CRMAuthorizationsConcurrentReviews,
  RoutePath.CRMReferralOutbound,
  RoutePath.CRMReferralOutboundCalendar,
  RoutePath.CRMMedicaidPending
];

export type ScreenerFacilityView = {
  kind: (typeof SCREENER_FACILITY_VIEW_KIND)[keyof typeof SCREENER_FACILITY_VIEW_KIND];
  facilityType: FacilityFacilityTypeEnum;
};

interface UseScreenerFacilityViewResult {
  view: ScreenerFacilityView;
  isScopeResolved: boolean;
  selectedFacilityId: string | null;
  setFacilityId: (facilityId: string) => void;
}

export const allFacilitiesIntentAtom = atomWithSession<{
  facilityType: FacilityFacilityTypeEnum;
} | null>('screenerAllFacilitiesIntent', null);

export const isValidFacilityType = (
  value: unknown
): value is FacilityFacilityTypeEnum => {
  return (
    typeof value === 'string' &&
    Object.values(FacilityFacilityTypeEnum).includes(
      value as FacilityFacilityTypeEnum
    )
  );
};

export const useUserCanUseAllFacilities = (pathname?: string): boolean => {
  const currentUser = useCurrentUser().data;
  return (
    !!currentUser &&
    (currentUser.user_facility_accesses?.length ?? 0) >=
      getMinimumFacilityCountForAllView(pathname)
  );
};

export const isOnAllFacilitiesSupportedPath = (pathname: string) =>
  isAutoDecisioningAdminHubPath(pathname) ||
  SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT.includes(pathname as RoutePath) ||
  isDataCenterV3Path(pathname);

const getAccessibleFacilityTypesFromFacilities = (
  facilities: FacilityPayload[] | undefined,
  accessibleFacilityIds: ReadonlySet<string>
): Set<FacilityFacilityTypeEnum> | null => {
  if (!facilities) {
    return null;
  }

  return facilities.reduce((types, facility) => {
    if (facility.facility_type && accessibleFacilityIds.has(facility.id)) {
      types.add(facility.facility_type);
    }
    return types;
  }, new Set<FacilityFacilityTypeEnum>());
};

const resolveAllFacilitiesIntentFacilityType = (
  intent: { facilityType: FacilityFacilityTypeEnum } | null,
  accessibleFacilityTypes: Set<FacilityFacilityTypeEnum> | null
): FacilityFacilityTypeEnum | null => {
  // The session intent can outlive facility access changes. Keep it when access
  // data is still loading or the requested type is accessible; otherwise only
  // auto-recover when there is exactly one accessible care setting to choose.
  if (!intent || !isValidFacilityType(intent.facilityType)) {
    return null;
  }

  if (
    accessibleFacilityTypes === null ||
    accessibleFacilityTypes.has(intent.facilityType)
  ) {
    return intent.facilityType;
  }

  if (accessibleFacilityTypes.size === 1) {
    return Array.from(accessibleFacilityTypes)[0] ?? null;
  }

  return null;
};

/**
 * Read-only access to the screener facility view + a setter that updates the
 * selected facility (without touching intent or URL). The atom is the source
 * of truth for all-facilities mode; URL is a derived side-effect synced by
 * `useFacilitySelectorController`.
 */
export const useScreenerFacilityView = (): UseScreenerFacilityViewResult => {
  const location = useLocation();
  const pathname = location.pathname;
  const isAutoDecisioningRoute = isAutoDecisioningAdminHubPath(pathname);

  const selectedFacilityId = useSelectedFacilityId();
  const [, setSelectedFacilityIdAtom] = useAtom(selectedFacilityIdAtom);
  const [intent, setIntent] = useAtom(allFacilitiesIntentAtom);
  const currentUser = useCurrentUser().data;
  const userCanUseAllFacilities = useUserCanUseAllFacilities(pathname);
  const accessibleFacilityIds = useAccessibleFacilityIds();

  const { data: facilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();

  const selectedFacility = facilitiesResponse?.facilities.find(
    (facility) => facility.id === selectedFacilityId
  );
  const specificFacilityType =
    selectedFacility?.facility_type ?? FacilityFacilityTypeEnum.SkilledNursing;

  const accessibleFacilityTypes = useMemo(() => {
    if (!currentUser) {
      return null;
    }

    return getAccessibleFacilityTypesFromFacilities(
      facilitiesResponse?.facilities,
      accessibleFacilityIds
    );
  }, [currentUser, facilitiesResponse?.facilities, accessibleFacilityIds]);

  const defaultFacilityId = currentUser
    ? getDefaultScreenerFacilityId(currentUser)
    : null;
  const defaultFacility = facilitiesResponse?.facilities.find(
    (facility) => facility.id === defaultFacilityId
  );
  const legacyIntentFacilityType =
    isAutoDecisioningRoute &&
    selectedFacilityId === ALL_FACILITIES &&
    defaultFacility &&
    (defaultFacility.id === currentUser?.default_screener_facility_id ||
      defaultFacility.id === currentUser?.facility_id ||
      accessibleFacilityTypes?.size === 1)
      ? defaultFacility.facility_type
      : null;

  const resolvedIntentFacilityType = useMemo(
    () =>
      resolveAllFacilitiesIntentFacilityType(
        intent ??
          (legacyIntentFacilityType
            ? { facilityType: legacyIntentFacilityType }
            : null),
        accessibleFacilityTypes
      ),
    [accessibleFacilityTypes, intent, legacyIntentFacilityType]
  );

  useEffect(() => {
    if (
      !isAutoDecisioningRoute ||
      selectedFacilityId !== ALL_FACILITIES ||
      !resolvedIntentFacilityType ||
      !accessibleFacilityTypes?.has(resolvedIntentFacilityType)
    ) {
      return;
    }
    const facility =
      defaultFacility?.facility_type === resolvedIntentFacilityType &&
      accessibleFacilityIds.has(defaultFacility.id)
        ? defaultFacility
        : facilitiesResponse?.facilities.find(
            (candidate) =>
              candidate.facility_type === resolvedIntentFacilityType &&
              accessibleFacilityIds.has(candidate.id)
          );
    if (!facility) {
      return;
    }
    setIntent({ facilityType: resolvedIntentFacilityType });
    setSelectedFacilityIdAtom(facility.id);
  }, [
    accessibleFacilityIds,
    accessibleFacilityTypes,
    defaultFacility,
    facilitiesResponse?.facilities,
    isAutoDecisioningRoute,
    resolvedIntentFacilityType,
    selectedFacilityId,
    setIntent,
    setSelectedFacilityIdAtom
  ]);

  useEffect(() => {
    if (!intent || accessibleFacilityTypes === null) {
      return;
    }

    if (
      resolvedIntentFacilityType &&
      resolvedIntentFacilityType !== intent.facilityType
    ) {
      setIntent({ facilityType: resolvedIntentFacilityType });
      return;
    }

    if (
      !resolvedIntentFacilityType &&
      !accessibleFacilityTypes.has(intent.facilityType)
    ) {
      setIntent(null);
    }
  }, [accessibleFacilityTypes, intent, resolvedIntentFacilityType, setIntent]);

  const view = useMemo<ScreenerFacilityView>(() => {
    if (
      !resolvedIntentFacilityType ||
      !userCanUseAllFacilities ||
      !isOnAllFacilitiesSupportedPath(pathname)
    ) {
      return {
        kind: SCREENER_FACILITY_VIEW_KIND.Specific,
        facilityType: specificFacilityType
      };
    }
    return {
      kind: SCREENER_FACILITY_VIEW_KIND.AllFacilities,
      facilityType: resolvedIntentFacilityType
    };
  }, [
    pathname,
    userCanUseAllFacilities,
    resolvedIntentFacilityType,
    specificFacilityType
  ]);

  const setFacilityId = useCallback(
    (facilityId: string) => {
      if (facilityId === selectedFacilityId) {
        return;
      }
      setSelectedFacilityIdAtom(facilityId);
      queryClient.getQueriesData([]).forEach(([queryKey]) => {
        const key0 = Array.isArray(queryKey) ? queryKey[0] : queryKey;
        if (
          key0 !== 'useCurrentUser' &&
          key0 !== 'screenerFacilitySettings' &&
          key0 !== SCREENER_SETTINGS_BATCH_KEY &&
          key0 !== SCREENER_SETTINGS_PER_FACILITY_KEY &&
          key0 !== FIND_ONE_SCREENER_SETTING_QUERY_KEY &&
          key0 !== DATA_CENTER_V3_QUERY_KEY &&
          key0 !== USE_FACILITIES_QUERY_KEY[0]
        ) {
          queryClient.resetQueries(queryKey);
        }
      });
    },
    [selectedFacilityId, setSelectedFacilityIdAtom]
  );

  return {
    view,
    isScopeResolved:
      !isAutoDecisioningRoute ||
      (view.kind === SCREENER_FACILITY_VIEW_KIND.AllFacilities
        ? Boolean(accessibleFacilityTypes?.has(view.facilityType))
        : Boolean(
            selectedFacility?.facility_type &&
              accessibleFacilityIds.has(selectedFacility.id)
          )),
    selectedFacilityId,
    setFacilityId
  };
};
